// New-trip wizard, step 1 (NewTripScreen): a toggle between a single
// destination (autocomplete + Depart/Return dates) and multi-city (a
// vertical route of stop cards, each with its own city and day count,
// dates computed and displayed live). Step 2 (PlanningPathScreen): choose
// scratch / brainstorm-ideas / preset planning path. Both are controlled by
// tripDraft state owned by App.js.
//
// Multi-city payload contract (unchanged from App.js's perspective): stop 0
// maps to `destination`/`primaryDays`, the rest map to `legs: [{destination,
// dayCount, overlapStart?}]`. overlapStart marks a stop whose first day is
// the same calendar day as the previous stop's last day (a shared travel
// day) — see computeLegSchedule in utils/legSchedule.js. See docs/state-model.md.
import React from 'react';
import { PALETTE, headerPad } from '../theme';
import { Icon, IconBtn, BigButton } from '../components/UI';
import { googleFlightsUrl } from '../utils/bookingLinks';
import { addDaysIso, daysBetweenIso, computeLegSchedule } from '../utils/legSchedule';

const today = new Date().toISOString().split('T')[0];

// Re-exported: App.js, itinerary/panels.js, and existing tests import these
// date helpers from here. Source of truth lives in utils/legSchedule.js
// (shared with logic.js/bookingLinks.js, which shouldn't import a screen file).
export { addDaysIso, daysBetweenIso };

const fieldStyle = {
  appearance: 'none', border: 0, outline: 'none', background: 'transparent',
  fontFamily: 'Fraunces, Georgia, serif', fontSize: 20, color: PALETTE.ink,
  width: '100%', padding: '14px 0',
};

const bareFieldStyle = {
  appearance: 'none', border: 0, outline: 'none', background: 'transparent',
  fontFamily: 'Fraunces, Georgia, serif', fontSize: 16, color: PALETTE.ink,
  width: '100%', padding: '2px 0',
};

function Field({ label, icon, flex, children }) {
  return (
    <div style={{ flex: flex ? 1 : undefined, minWidth: 0 }}>
      <div style={{
        fontFamily: 'JetBrains Mono, monospace', fontSize: 9,
        letterSpacing: 1.5, textTransform: 'uppercase', color: PALETTE.inkMute, marginBottom: 4,
      }}>{label}</div>
      <div style={{
        display: 'flex', alignItems: 'center',
        borderBottom: `1px solid ${PALETTE.rule}`,
        gap: icon ? 8 : 0,
      }}>
        {icon && <Icon name={icon} size={16} style={{ color: PALETTE.inkMute, flexShrink: 0 }} />}
        {children}
      </div>
    </div>
  );
}

// ── Custom date picker ────────────────────────────────────────────────────────
const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const MONTH_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const DOW = ['S','M','T','W','T','F','S'];
const DOW_SHORT = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
function weekdayShort(iso) {
  return iso ? DOW_SHORT[new Date(iso + 'T12:00:00').getDay()] : '';
}

export function CalPicker({ label, value, onChange, min, openTrigger, disabled = false, hint }) {
  const [open, setOpen] = React.useState(false);
  const wrapRef = React.useRef(null);

  React.useEffect(() => {
    if (openTrigger > 0) setOpen(true);
  }, [openTrigger]); // eslint-disable-line

  const parsed = value ? new Date(value + 'T00:00:00') : null;
  const todayDate = React.useMemo(() => { const d = new Date(); d.setHours(0,0,0,0); return d; }, []);

  const initMonth = () => {
    if (parsed) return { y: parsed.getFullYear(), m: parsed.getMonth() };
    if (min) { const d = new Date(min + 'T00:00:00'); return { y: d.getFullYear(), m: d.getMonth() }; }
    return { y: todayDate.getFullYear(), m: todayDate.getMonth() };
  };
  const [view, setView] = React.useState(initMonth);

  React.useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  React.useEffect(() => {
    if (!value && min) {
      const d = new Date(min + 'T00:00:00');
      setView({ y: d.getFullYear(), m: d.getMonth() });
    }
  }, [min]); // eslint-disable-line

  const prevMonth = () => setView(v => v.m === 0 ? { y: v.y - 1, m: 11 } : { y: v.y, m: v.m - 1 });
  const nextMonth = () => setView(v => v.m === 11 ? { y: v.y + 1, m: 0 } : { y: v.y, m: v.m + 1 });

  const firstDow = new Date(view.y, view.m, 1).getDay();
  const daysInMonth = new Date(view.y, view.m + 1, 0).getDate();
  const minDate = min ? new Date(min + 'T00:00:00') : null;

  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const toIso = (d) => `${view.y}-${String(view.m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  const isDisabled = (d) => minDate ? new Date(view.y, view.m, d) < minDate : false;
  const isSelected = (d) => parsed && parsed.getFullYear() === view.y && parsed.getMonth() === view.m && parsed.getDate() === d;
  const isToday = (d) => todayDate.getFullYear() === view.y && todayDate.getMonth() === view.m && todayDate.getDate() === d;

  const displayVal = parsed
    ? `${DOW_SHORT[parsed.getDay()]}, ${MONTH_SHORT[parsed.getMonth()]} ${parsed.getDate()}, ${parsed.getFullYear()}`
    : null;

  return (
    <div ref={wrapRef} style={{ flex: 1, minWidth: 0, position: 'relative' }}>
      <div style={{
        fontFamily: 'JetBrains Mono', fontSize: 9,
        letterSpacing: 1.5, textTransform: 'uppercase', color: PALETTE.inkMute, marginBottom: 4,
      }}>{label}</div>
      <div style={{ borderBottom: `1px solid ${PALETTE.rule}`, display: 'flex', alignItems: 'center', gap: 8 }}>
        <Icon name="cal" size={16} style={{ color: PALETTE.inkMute, flexShrink: 0 }} />
        <button
          onClick={() => !disabled && setOpen(o => !o)}
          style={{
            appearance: 'none', border: 0, background: 'transparent', cursor: disabled ? 'default' : 'pointer',
            fontFamily: 'Inter', fontSize: 15,
            color: displayVal ? (disabled ? PALETTE.inkSoft : PALETTE.ink) : PALETTE.inkMute,
            padding: '14px 0', textAlign: 'left', width: '100%',
          }}
        >{displayVal || 'Select date'}</button>
        {value && !disabled && (
          <button onClick={() => onChange('')} style={{
            appearance: 'none', border: 0, background: 'transparent',
            cursor: 'pointer', color: PALETTE.inkMute, padding: '4px', flexShrink: 0,
          }}>
            <Icon name="x" size={14} />
          </button>
        )}
      </div>
      {hint && (
        <div style={{ fontFamily: 'Inter', fontSize: 10.5, color: PALETTE.inkMute, marginTop: 4 }}>{hint}</div>
      )}

      {open && !disabled && (
        <div style={{
          position: 'absolute', top: '100%', zIndex: 300,
          left: 0,
          background: PALETTE.card, borderRadius: 16,
          border: `1px solid ${PALETTE.rule}`,
          boxShadow: '0 10px 36px rgba(34,31,26,0.18)',
          padding: '14px 12px', marginTop: 8, width: 264,
        }}>
          {/* Month nav */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <button onClick={prevMonth} aria-label="Previous month" style={{
              appearance: 'none', border: 0, background: 'transparent',
              cursor: 'pointer', color: PALETTE.inkMute, padding: '4px 8px', borderRadius: 8,
            }}>
              <Icon name="arrowL" size={14} />
            </button>
            <div style={{ fontFamily: 'Fraunces', fontSize: 15, color: PALETTE.ink, letterSpacing: -0.2 }}>
              {MONTH_NAMES[view.m]} {view.y}
            </div>
            <button onClick={nextMonth} aria-label="Next month" style={{
              appearance: 'none', border: 0, background: 'transparent',
              cursor: 'pointer', color: PALETTE.inkMute, padding: '4px 8px', borderRadius: 8,
            }}>
              <Icon name="arrow" size={14} />
            </button>
          </div>

          {/* Day-of-week headers */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: 4 }}>
            {DOW.map((d, i) => (
              <div key={i} style={{
                fontFamily: 'JetBrains Mono', fontSize: 8, letterSpacing: 1,
                textAlign: 'center', color: PALETTE.inkMute, padding: '3px 0',
              }}>{d}</div>
            ))}
          </div>

          {/* Day cells */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
            {cells.map((d, i) => {
              if (d === null) return <div key={i} />;
              const disabled = isDisabled(d);
              const selected = isSelected(d);
              const tdy = isToday(d);
              return (
                <button
                  key={i}
                  onClick={() => { if (!disabled) { onChange(toIso(d)); setOpen(false); } }}
                  style={{
                    appearance: 'none', border: 'none', cursor: disabled ? 'default' : 'pointer',
                    borderRadius: 999, padding: '6px 0', textAlign: 'center',
                    background: selected ? PALETTE.accent : 'transparent',
                    color: disabled ? PALETTE.rule : selected ? '#fff' : PALETTE.ink,
                    fontFamily: 'Inter', fontSize: 13, fontWeight: selected ? 600 : 400,
                    outline: tdy && !selected ? `2px solid ${PALETTE.accent}` : 'none',
                    outlineOffset: '-2px',
                    transition: 'background 0.1s',
                  }}
                  onMouseEnter={e => { if (!disabled && !selected) e.currentTarget.style.background = PALETTE.paperDim; }}
                  onMouseLeave={e => { if (!selected) e.currentTarget.style.background = 'transparent'; }}
                >{d}</button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Flight search links ────────────────────────────────────────────────────────
function buildFlightLinks(destination, travelers, dateStart, dateEnd) {
  return [
    {
      label: 'Google Flights',
      detail: 'Compare all airlines · open in Google Flights',
      url: googleFlightsUrl(destination || '', dateStart, dateEnd, travelers || 1),
      badge: 'recommended',
    },
  ];
}

// Compact native date field used inside multi-city stop cards — Arrives/
// Departs pickers replace the old +/- day-count stepper so each leg's
// dates can be set directly instead of reverse-engineering a day count.
function MiniDateField({ label, ariaLabel, value, min, max, disabled, onChange }) {
  const weekday = weekdayShort(value);
  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{
        fontFamily: 'JetBrains Mono, monospace', fontSize: 8.5,
        letterSpacing: 1, textTransform: 'uppercase', color: PALETTE.inkMute, marginBottom: 3,
      }}>
        {label}{weekday && <span style={{ color: PALETTE.inkSoft }}> · {weekday}</span>}
      </div>
      <input
        type="date"
        aria-label={ariaLabel || label}
        value={value || ''}
        min={min || undefined}
        max={max || undefined}
        disabled={disabled}
        onChange={e => e.target.value && onChange(e.target.value)}
        style={{
          appearance: 'none', border: `1px solid ${PALETTE.rule}`, borderRadius: 6,
          padding: '5px 6px', background: disabled ? PALETTE.paperDim : PALETTE.paper,
          fontFamily: 'JetBrains Mono, monospace', fontSize: 11,
          color: disabled ? PALETTE.inkMute : PALETTE.ink,
          outline: 'none', width: '100%', colorScheme: 'light',
        }}
      />
    </div>
  );
}

function PathCard({ id, title, subtitle, icon, selected, onSelect }) {
  return (
    <button onClick={() => onSelect(id)} style={{
      appearance: 'none', cursor: 'pointer',
      flex: 1, padding: '20px 16px', borderRadius: 14, textAlign: 'left',
      border: selected ? `2px solid ${PALETTE.ink}` : `1.5px solid ${PALETTE.rule}`,
      background: selected ? PALETTE.paperDim : PALETTE.card,
      transition: 'all 0.15s',
    }}>
      <div style={{
        width: 40, height: 40, borderRadius: 10,
        background: selected ? PALETTE.ink : PALETTE.paperDim,
        color: selected ? PALETTE.paper : PALETTE.ink,
        display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14,
      }}>
        <Icon name={icon} size={20} stroke={1.5} />
      </div>
      <div style={{ fontFamily: 'Fraunces', fontSize: 18, letterSpacing: -0.3, color: PALETTE.ink, lineHeight: 1.2, marginBottom: 6 }}>
        {title}
      </div>
      <div style={{ fontFamily: 'Inter', fontSize: 12, color: PALETTE.inkSoft, lineHeight: 1.5 }}>
        {subtitle}
      </div>
    </button>
  );
}

// bare=true renders just the input + suggestions dropdown, no Field label
// wrapper — used inside multi-city stop cards which supply their own layout.
function DestinationAutocomplete({ value, onChange, bare = false }) {
  const [suggestions, setSuggestions] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const debounceRef = React.useRef(null);

  const handleChange = (e) => {
    const val = e.target.value;
    onChange(val);
    clearTimeout(debounceRef.current);
    if (val.length < 2) { setSuggestions([]); setOpen(false); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(val)}&format=json&addressdetails=1&limit=6&accept-language=en`;
        const resp = await fetch(url, { headers: { 'User-Agent': 'TravelPlannerApp/1.0' } });
        const data = await resp.json();
        const seen = new Set();
        const results = data
          .filter(r => ['city','town','village','municipality'].includes(r.type) || r.class === 'place' || r.class === 'boundary')
          .map(r => {
            const city = r.address?.city || r.address?.town || r.address?.village || r.name;
            const country = r.address?.country || '';
            const state = r.address?.state || '';
            return {
              id: r.place_id,
              main: city,
              secondary: [state, country].filter(Boolean).join(', '),
              full: [city, country].filter(Boolean).join(', '),
            };
          })
          .filter(r => { if (seen.has(r.full)) return false; seen.add(r.full); return true; })
          .slice(0, 5);
        setSuggestions(results);
        setOpen(results.length > 0);
      } catch {
        setSuggestions([]);
      }
    }, 350);
  };

  const handleSelect = (s) => {
    onChange(s.full);
    setSuggestions([]);
    setOpen(false);
  };

  const input = (
    <input
      value={value}
      onChange={handleChange}
      onFocus={() => suggestions.length > 0 && setOpen(true)}
      onBlur={() => setTimeout(() => setOpen(false), 150)}
      placeholder="City or country"
      autoComplete="off"
      style={bare ? bareFieldStyle : fieldStyle}
    />
  );

  return (
    <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
      {bare ? input : <Field label="Destination" icon="pin">{input}</Field>}
      {open && suggestions.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200,
          background: PALETTE.card, borderRadius: 12,
          border: `1px solid ${PALETTE.rule}`,
          boxShadow: '0 8px 24px rgba(34,31,26,0.14)',
          marginTop: 6, overflow: 'hidden',
        }}>
          {suggestions.map((s, i) => (
            <button
              key={s.id}
              onMouseDown={() => handleSelect(s)}
              style={{
                display: 'flex', flexDirection: 'column',
                width: '100%', textAlign: 'left',
                padding: '11px 14px', background: 'transparent',
                border: 0,
                borderBottom: i < suggestions.length - 1 ? `0.5px solid ${PALETTE.ruleSoft}` : 0,
                cursor: 'pointer',
              }}
              onMouseEnter={e => e.currentTarget.style.background = PALETTE.paperDim}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <span style={{ fontFamily: 'Fraunces', fontSize: 16, color: PALETTE.ink, letterSpacing: -0.2 }}>
                {s.main}
              </span>
              <span style={{ fontFamily: 'Inter', fontSize: 12, color: PALETTE.inkMute, marginTop: 1 }}>
                {s.secondary}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Trip type toggle ──────────────────────────────────────────────────────────
function TripTypeToggle({ value, onChange }) {
  const opts = [
    { id: 'single', label: 'One destination' },
    { id: 'multi', label: 'Multi-city' },
  ];
  return (
    <div style={{ display: 'flex', gap: 4, padding: 4, background: PALETTE.paperDim, borderRadius: 12 }}>
      {opts.map(o => {
        const active = value === o.id;
        return (
          <button
            key={o.id}
            onClick={() => onChange(o.id)}
            style={{
              flex: 1, appearance: 'none', border: 0, cursor: 'pointer',
              padding: '10px 0', borderRadius: 9,
              background: active ? PALETTE.ink : 'transparent',
              color: active ? PALETTE.paper : PALETTE.inkSoft,
              fontFamily: 'Inter', fontSize: 13, fontWeight: 600,
              transition: 'background 0.15s, color 0.15s',
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// ── Multi-city route builder ──────────────────────────────────────────────────
// A vertical route of numbered stop cards connected by a timeline line —
// stop 0 is the primary destination. Each card's date range is computed
// live from `dateStart` + the cumulative day count of the stops before it,
// so the dates are always visible without needing a date picker per stop.
export function MultiCityStops({ stops, setStops, dateStart, setDateStart }) {
  const addStop = () => setStops(s => [...s, { id: `stop_${Date.now()}`, destination: '', dayCount: 2 }]);
  const updateStop = (id, patch) => setStops(s => s.map(x => (x.id === id ? { ...x, ...patch } : x)));
  const removeStop = (id) => setStops(s => (s.length > 2 ? s.filter(x => x.id !== id) : s));

  const { offsets, totalDays } = computeLegSchedule(stops);

  // Departs is the stop's own end date — directly sets ITS OWN dayCount, no
  // need to touch any other stop (offsets recompute from dayCount on every
  // render, so later stops shift automatically).
  const setStopDepartDate = (stopIdx, newDateIso) => {
    if (!dateStart || !newDateIso) return;
    const newOffset = daysBetweenIso(dateStart, newDateIso);
    const ownStart = offsets[stopIdx];
    if (newOffset < ownStart) return; // can't depart before arriving
    updateStop(stops[stopIdx].id, { dayCount: newOffset - ownStart + 1 });
  };

  // Arrives is constrained to the previous stop's own last day (an
  // overlapping travel day — you can be in both cities the same calendar
  // day) or the day right after (the default, no overlap). Landing on the
  // previous stop's last day sets overlapStart; the day after clears it.
  // This never touches the previous stop's own dayCount — each stop's span
  // is edited only through its own Departs field.
  const setStopArrivalDate = (stopIdx, newDateIso) => {
    if (stopIdx === 0 || !dateStart || !newDateIso) return;
    const newOffset = daysBetweenIso(dateStart, newDateIso);
    const prevStop = stops[stopIdx - 1];
    const prevLastDay = offsets[stopIdx - 1] + (prevStop.dayCount || 0) - 1;
    updateStop(stops[stopIdx].id, { overlapStart: newOffset <= prevLastDay });
  };

  return (
    <div>
      <CalPicker
        label="Trip starts"
        value={dateStart}
        min={today}
        onChange={setDateStart}
      />

      <div style={{ marginTop: 22 }}>
        {stops.map((stop, i) => {
          const isLast = i === stops.length - 1;
          const arriveIso = dateStart ? addDaysIso(dateStart, offsets[i]) : '';
          const departIso = dateStart ? addDaysIso(dateStart, offsets[i] + (stop.dayCount || 0) - 1) : '';
          const prevStop = i > 0 ? stops[i - 1] : null;
          const prevLastDayIso = prevStop && dateStart
            ? addDaysIso(dateStart, offsets[i - 1] + (prevStop.dayCount || 0) - 1)
            : undefined;
          return (
            <div key={stop.id} style={{ display: 'flex', gap: 12 }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                <div style={{
                  width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                  background: PALETTE.ink, color: PALETTE.paper,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: 'JetBrains Mono', fontSize: 11, fontWeight: 700,
                }}>{i + 1}</div>
                {!isLast && <div style={{ width: 2, flex: 1, background: PALETTE.rule, marginTop: 2 }} />}
              </div>
              <div style={{ flex: 1, minWidth: 0, paddingBottom: isLast ? 0 : 14 }}>
                <div style={{ border: `1px solid ${PALETTE.rule}`, borderRadius: 14, padding: '12px 14px', background: PALETTE.card }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <DestinationAutocomplete bare value={stop.destination} onChange={v => updateStop(stop.id, { destination: v })} />
                    {stops.length > 2 && (
                      <button onClick={() => removeStop(stop.id)} title="Remove stop" style={{ appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer', color: PALETTE.inkMute, padding: 4, flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                        <Icon name="x" size={14} />
                      </button>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8, paddingTop: 8, borderTop: `0.5px solid ${PALETTE.ruleSoft}` }}>
                    <MiniDateField
                      label="Arrives"
                      ariaLabel={`Arrives — stop ${i + 1}`}
                      value={arriveIso}
                      min={prevLastDayIso}
                      max={prevLastDayIso ? addDaysIso(prevLastDayIso, 1) : undefined}
                      disabled={i === 0 || !dateStart}
                      onChange={v => setStopArrivalDate(i, v)}
                    />
                    <MiniDateField
                      label="Departs"
                      ariaLabel={`Departs — stop ${i + 1}`}
                      value={departIso}
                      min={arriveIso || undefined}
                      disabled={!dateStart}
                      onChange={v => setStopDepartDate(i, v)}
                    />
                  </div>
                  <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 10, color: PALETTE.inkMute, marginTop: 6, letterSpacing: 0.3 }}>
                    {stop.dayCount || 0} day{stop.dayCount !== 1 ? 's' : ''}
                  </div>
                  {i > 0 && stop.overlapStart && (
                    <div style={{ fontFamily: 'Inter', fontSize: 10.5, color: PALETTE.inkMute, marginTop: 4 }}>
                      Shared travel day with {(prevStop?.destination || 'the previous stop').split(',')[0].trim()}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ paddingLeft: 38 }}>
        <button
          onClick={addStop}
          style={{
            appearance: 'none', cursor: 'pointer', width: '100%',
            padding: '10px 0', borderRadius: 10, border: `1.5px dashed ${PALETTE.rule}`,
            background: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
            fontFamily: 'Inter', fontSize: 13, fontWeight: 500, color: PALETTE.inkSoft,
          }}
        >
          <Icon name="plus" size={14} />
          Add another city
        </button>

        <div style={{ fontFamily: 'Inter', fontSize: 12, color: PALETTE.inkSoft, marginTop: 12 }}>
          {totalDays} day{totalDays !== 1 ? 's' : ''} total across {stops.length} cities
        </div>
      </div>
    </div>
  );
}

export function PlanningPathScreen({ isMobile, destination, onBack, onChoose }) {
  return (
    <div style={{ background: PALETTE.paper, minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <div style={{
        position: 'sticky', top: 0, zIndex: 4, background: PALETTE.paper,
        padding: headerPad(isMobile),
        borderBottom: `0.5px solid ${PALETTE.ruleSoft}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minHeight: 36 }}>
          <IconBtn name="arrowL" label="Back" onClick={onBack} />
          <div style={{ fontFamily: 'JetBrains Mono', fontSize: 11, color: PALETTE.inkMute, letterSpacing: 1, textTransform: 'uppercase' }}>
            {destination ? destination.split(',')[0] : 'New trip'}
          </div>
        </div>
      </div>

      <div style={{ flex: 1, padding: '32px 24px 40px', display: 'flex', flexDirection: 'column' }}>
        <div style={{ fontFamily: 'Fraunces, Georgia, serif', fontSize: 34, fontWeight: 400, letterSpacing: -0.8, color: PALETTE.ink, lineHeight: 1.05 }}>
          How would you<br />like to start?
        </div>
        <div style={{ fontFamily: 'Inter', fontSize: 14, color: PALETTE.inkSoft, marginTop: 10, lineHeight: 1.6 }}>
          Choose a starting point — we'll help you build from there.
        </div>

        <div style={{ marginTop: 36, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <PathCard
            id="scratch"
            title="Create my own itinerary from scratch"
            subtitle="Start with a blank canvas and add exactly what you want, your way."
            icon="pencil"
            selected={false}
            onSelect={onChoose}
          />
          <PathCard
            id="brainstorm"
            title="Give me ideas"
            subtitle="Browse curated activities for your destination and pick what sounds good."
            icon="sparkle"
            selected={false}
            onSelect={onChoose}
          />
          <PathCard
            id="preset"
            title="Choose from preset"
            subtitle="Pick a travel style — foodie, culture buff, off the beaten path — and we'll assemble a matching itinerary."
            icon="list"
            selected={false}
            onSelect={onChoose}
          />
        </div>
      </div>
    </div>
  );
}

export function NewTripScreen({ isMobile, draft, onDraftChange, onBack, onContinue }) {
  const [tripType, setTripType] = React.useState(draft?.legs?.length > 0 ? 'multi' : 'single');

  // Single-destination mode state
  const [dest, setDest] = React.useState(draft?.destination || '');
  const [dateStart, setDateStart] = React.useState(draft?.legs?.length > 0 ? '' : (draft?.dateStart || ''));
  const [dateEnd, setDateEnd] = React.useState(draft?.legs?.length > 0 ? '' : (draft?.dateEnd || ''));
  const [returnOpenTrigger, setReturnOpenTrigger] = React.useState(0);

  // Multi-city mode state — `stops` always has >= 2 entries; stop 0 is primary.
  const [stops, setStops] = React.useState(() => {
    if (draft?.legs?.length > 0) {
      return [
        { id: 'stop_0', destination: draft.destination || '', dayCount: draft.primaryDays || 3 },
        ...draft.legs.map((l, i) => ({ id: `stop_${i + 1}`, destination: l.destination || '', dayCount: l.dayCount || 2, overlapStart: !!l.overlapStart })),
      ];
    }
    return [
      { id: 'stop_0', destination: '', dayCount: 3 },
      { id: 'stop_1', destination: '', dayCount: 2 },
    ];
  });
  const [multiStart, setMultiStart] = React.useState(draft?.legs?.length > 0 ? (draft?.dateStart || '') : '');

  const [travelers, setTravelers] = React.useState(draft?.travelers || 2);
  const [flightExpanded, setFlightExpanded] = React.useState(false);

  const topPad = headerPad(isMobile);

  const flightLinks = buildFlightLinks(dest, travelers, dateStart, dateEnd);

  React.useEffect(() => {
    if (tripType === 'single' && dest.trim() && dateStart) setFlightExpanded(true);
  }, [dest, dateStart, tripType]);

  // Carry the primary destination/start date across when switching modes so
  // toggling back and forth doesn't lose what's already been typed.
  const switchToMulti = () => {
    setTripType('multi');
    setStops(s => (s[0].destination ? s : [{ ...s[0], destination: dest }, s[1]]));
    setMultiStart(v => v || dateStart);
  };
  const switchToSingle = () => {
    setTripType('single');
    if (!dest && stops[0]?.destination) setDest(stops[0].destination);
    if (!dateStart && multiStart) setDateStart(multiStart);
  };

  const headerCity = tripType === 'multi'
    ? (stops.find(s => s.destination.trim())?.destination.split(',')[0] || 'New trip')
    : (dest ? dest.split(',')[0] : 'New trip');

  const canContinue = tripType === 'multi'
    ? stops.every(s => s.destination.trim())
    : !!dest.trim();

  const handleContinue = () => {
    let data;
    if (tripType === 'multi') {
      const [first, ...rest] = stops;
      const { totalDays } = computeLegSchedule(stops);
      data = {
        destination: first.destination,
        dateStart: multiStart,
        dateEnd: multiStart ? addDaysIso(multiStart, totalDays - 1) : '',
        travelers,
        legs: rest.map(s => ({ destination: s.destination, dayCount: s.dayCount, overlapStart: !!s.overlapStart })),
        primaryDays: first.dayCount,
      };
    } else {
      data = { destination: dest, dateStart, dateEnd, travelers, legs: [], primaryDays: undefined };
    }
    onDraftChange(data);
    onContinue?.(data);
  };

  return (
    <div style={{ background: PALETTE.paper, minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <div style={{
        position: 'sticky', top: 0, zIndex: 4, background: PALETTE.paper,
        padding: topPad, borderBottom: `0.5px solid ${PALETTE.ruleSoft}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minHeight: 36 }}>
          <IconBtn name="arrowL" label="Back" onClick={onBack} />
          <div style={{ fontFamily: 'JetBrains Mono', fontSize: 11, color: PALETTE.inkMute, letterSpacing: 1, textTransform: 'uppercase' }}>
            {headerCity}
          </div>
        </div>
      </div>

      <div style={{ padding: '24px 24px 0', flex: 1 }}>
        <div style={{ fontFamily: 'Fraunces, Georgia, serif', fontSize: 34, fontWeight: 400, letterSpacing: -0.8, color: PALETTE.ink, lineHeight: 1.05 }}>
          Tell us where<br />and when.
        </div>

        <div style={{ marginTop: 28 }}>
          <TripTypeToggle value={tripType} onChange={v => (v === 'multi' ? switchToMulti() : switchToSingle())} />
        </div>

        <div style={{ marginTop: 24 }}>
          {tripType === 'single' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <DestinationAutocomplete value={dest} onChange={setDest} />
              <div style={{ display: 'flex', gap: 16 }}>
                <CalPicker
                  label="Depart"
                  value={dateStart}
                  min={today}
                  onChange={v => {
                    setDateStart(v);
                    if (dateEnd && v > dateEnd) setDateEnd('');
                    if (v) setReturnOpenTrigger(t => t + 1);
                  }}
                />
                <CalPicker
                  label="Return"
                  value={dateEnd}
                  min={dateStart || today}
                  onChange={setDateEnd}
                  openTrigger={returnOpenTrigger}
                />
              </div>
            </div>
          ) : (
            <MultiCityStops stops={stops} setStops={setStops} dateStart={multiStart} setDateStart={setMultiStart} />
          )}
        </div>

        <div style={{ marginTop: 28 }}>
          <Field label="Travelers">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0' }}>
              <button onClick={() => setTravelers(Math.max(1, travelers - 1))} style={{
                appearance: 'none', border: `1px solid ${PALETTE.rule}`, cursor: 'pointer',
                width: 28, height: 28, borderRadius: 8, background: PALETTE.card,
                display: 'flex', alignItems: 'center', justifyContent: 'center', color: PALETTE.ink,
              }}>−</button>
              <span style={{ fontFamily: 'Fraunces', fontSize: 22, color: PALETTE.ink, minWidth: 20, textAlign: 'center' }}>{travelers}</span>
              <button onClick={() => setTravelers(travelers + 1)} style={{
                appearance: 'none', border: `1px solid ${PALETTE.rule}`, cursor: 'pointer',
                width: 28, height: 28, borderRadius: 8, background: PALETTE.card,
                display: 'flex', alignItems: 'center', justifyContent: 'center', color: PALETTE.ink,
              }}>+</button>
              <span style={{ fontFamily: 'Inter', fontSize: 13, color: PALETTE.inkMute }}>
                {travelers === 1 ? 'solo' : travelers === 2 ? 'you + 1' : `${travelers} people`}
              </span>
            </div>
          </Field>
        </div>

        {/* Flight search links — single-destination only; multi-city legs
            need per-hop search which isn't modeled here yet. */}
        {tripType === 'single' && (
          <div style={{ marginTop: 28 }}>
            <button
              onClick={() => setFlightExpanded(e => !e)}
              style={{
                appearance: 'none', border: 0, cursor: 'pointer', background: 'transparent',
                width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                marginBottom: flightExpanded ? 12 : 0, padding: 0,
              }}
            >
              <div>
                <div style={{ fontFamily: 'JetBrains Mono', fontSize: 10, color: PALETTE.inkMute, letterSpacing: 1.2, textTransform: 'uppercase', textAlign: 'left' }}>
                  Getting there · from SIN
                </div>
                <div style={{ fontFamily: 'Fraunces', fontSize: 19, color: PALETTE.ink, letterSpacing: -0.3, marginTop: 4, textAlign: 'left' }}>
                  {dest ? `Flights to ${dest.split(',')[0]}` : 'Search flights'}
                </div>
              </div>
              <Icon name={flightExpanded ? 'arrowUp' : 'arrowDn'} size={18} style={{ color: PALETTE.inkMute }} />
            </button>

            {flightExpanded && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                {flightLinks.map(link => (
                  <a
                    key={link.label}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '12px 14px', borderRadius: 12, textDecoration: 'none',
                      border: `1px solid ${PALETTE.rule}`, background: PALETTE.card,
                    }}
                  >
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                        <div style={{ fontFamily: 'Fraunces', fontSize: 17, color: PALETTE.ink, letterSpacing: -0.2 }}>{link.label}</div>
                        {link.badge && (
                          <div style={{
                            background: PALETTE.accent, color: '#fff',
                            fontFamily: 'Inter', fontSize: 9, fontWeight: 700,
                            letterSpacing: 0.5, textTransform: 'uppercase',
                            padding: '2px 6px', borderRadius: 4,
                          }}>{link.badge}</div>
                        )}
                      </div>
                      <div style={{ fontFamily: 'Inter', fontSize: 12, color: PALETTE.inkSoft }}>{link.detail}</div>
                    </div>
                    <Icon name="arrow" size={16} style={{ color: PALETTE.inkMute, flexShrink: 0 }} />
                  </a>
                ))}
              </div>
            )}

            {!flightExpanded && dest && dateStart && (
              <div style={{ marginTop: 10, padding: '10px 14px', borderRadius: 10, background: PALETTE.card, border: `0.5px solid ${PALETTE.ruleSoft}` }}>
                <div style={{ fontFamily: 'Inter', fontSize: 12, color: PALETTE.inkSoft }}>
                  SIN → {dest.split(',')[0]}{dateStart ? ` · ${dateStart}` : ''}{dateEnd ? ` – ${dateEnd}` : ''} · {travelers} pax
                </div>
                <div style={{ fontFamily: 'Inter', fontSize: 11, color: PALETTE.inkMute, marginTop: 3 }}>
                  Tap above to search Google Flights, Skyscanner & more
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div style={{ padding: '20px 24px 32px' }}>
        <BigButton
          onClick={handleContinue}
          disabled={!canContinue}
          style={{ background: canContinue ? PALETTE.ink : PALETTE.rule, color: PALETTE.paper }}
        >
          Start planning <Icon name="arrow" size={18} />
        </BigButton>
      </div>
    </div>
  );
}
